import { isRouteErrorResponse, useRouteError } from "@remix-run/react";
import { useEffect } from "react";

import ErrorPage from "./components/errorPage/ErrorPage";
import notFound from "./components/httpErrors/notFoundError/i18n";
import serverError from "./components/httpErrors/serverError/i18n";
import unauthorized from "./components/httpErrors/unauthorizedError/i18n";
import unknown from "./components/httpErrors/unknownError/i18n";
import { useConsent } from "./context/ConsentContext";
import { useLanguage } from "./context/LanguageContext";

export default function ErrorBoundaryFunction() {
  const error = useRouteError();
  const { lang } = useLanguage();
  const { consent } = useConsent();

  // Reporting is a side effect, so it belongs in an effect rather than in the
  // render pass. Sentry loads lazily, and only if the visitor opted in.
  useEffect(() => {
    if (!consent.analytics) return;
    if (isRouteErrorResponse(error) && error.status === 404) return;

    void import("@sentry/remix").then((Sentry) => {
      if (Sentry.getClient()) Sentry.captureException(error);
    });
  }, [error, consent.analytics]);

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      const t = notFound[lang];
      return (
        <ErrorPage status={404} title={t.title} description={t.description} />
      );
    }

    if (error.status === 401 || error.status === 403) {
      const t = unauthorized[lang];
      return (
        <ErrorPage
          status={error.status}
          title={t.title}
          description={t.description}
        />
      );
    }

    if (error.status >= 500) {
      const t = serverError[lang];
      return (
        <ErrorPage
          status={error.status}
          title={t.title}
          description={t.description}
        />
      );
    }
  }

  const t = unknown[lang];
  return <ErrorPage title={t.title} description={t.description} />;
}
